/**
 * src/data/LeadershipMicroBank.js
 * Hardcoded scenario bank for Module 5: Leadership Micro (Extreme Ownership)
 */

export const leadershipMicroBank = [
  {
    "id": "lead_01",
    "scenarioContext": "You discover a massive calculation error in a Q3 financial report that your manager is supposed to present to the board in 24 hours.",
    "options": [
      {
        "actionText": "I will recalculate the model, build a corrected slide deck, and email the manager the exact variance analysis alongside the updated slides.",
        "category": "Optimal",
        "feedback": "Correct. Extreme ownership. You did not just deliver the problem; you completely absorbed the cognitive burden of fixing it and delivered a finalized solution."
      },
      {
        "actionText": "I will immediately forward the broken spreadsheet to my manager, flag the error, and ask how they want me to handle it.",
        "category": "Problem Delivery",
        "feedback": "Failure. Upward delegation. By delivering a raw problem 24 hours before a board meeting, you forced your manager to do your job."
      },
      {
        "actionText": "I will schedule an urgent 30-minute sync with my manager and the data team to ensure we are all perfectly aligned on how to fix the formulas.",
        "category": "Approval Paralysis",
        "feedback": "Failure. While well-intentioned, demanding a 30-minute meeting during an executive crisis shows a fear of autonomous execution and wastes critical triage time."
      },
      {
        "actionText": "I will email the analytics department who supplied the raw data and tell them they need to fix their numbers before the board meeting.",
        "category": "Accountability Void",
        "feedback": "Failure. Blame shifting. Even if analytics provided bad data, you own the final report. Throwing colleagues under the bus does not solve the executive's immediate problem."
      }
    ]
  },
  {
    "id": "lead_02",
    "scenarioContext": "A critical software vendor drops out 48 hours before a massive enterprise rollout, citing unresolvable API conflicts.",
    "options": [
      {
        "actionText": "I will source two backup vendors, draft an impact analysis on cost and deployment delays for Option A vs. Option B, recommend Option B, and present it for immediate sign-off.",
        "category": "Optimal",
        "feedback": "Correct. Extreme ownership. You absorbed the panic, quantified the operational alternatives, and presented a binary choice to leadership, preserving their cognitive bandwidth."
      },
      {
        "actionText": "I will email my manager immediately stating that the vendor dropped out and ask them what our backup plan is for the rollout.",
        "category": "Problem Delivery",
        "feedback": "Failure. Upward delegation. You transferred a massive operational crisis directly onto your manager's desk without doing any investigative legwork."
      },
      {
        "actionText": "I will schedule a 1-hour cross-functional crisis meeting with IT, Operations, and Management to brainstorm vendor alternatives together.",
        "category": "Approval Paralysis",
        "feedback": "Failure. Brainstorming by committee during a 48-hour crisis creates catastrophic delays. You abdicated your responsibility to formulate a plan."
      },
      {
        "actionText": "I will notify leadership that Procurement chose a flaky vendor and state that this delay is entirely due to their poor vetting process.",
        "category": "Accountability Void",
        "feedback": "Failure. Blame shifting. Weaponizing a crisis to point fingers at another department destroys horizontal trust and offers zero operational solutions."
      }
    ]
  },
  {
    "id": "lead_03",
    "scenarioContext": "A tier-one client demands a major scope change on Friday afternoon for a project due on Monday morning.",
    "options": [
      {
        "actionText": "I will draft an impact analysis showing Option A (delay delivery by 1 week) and Option B (charge a 30% rush fee for weekend work). I will recommend Option B and send it to my manager for approval.",
        "category": "Optimal",
        "feedback": "Correct. Extreme ownership. You translated an unreasonable client demand into quantifiable business trade-offs, enabling your manager to simply approve the boundary-setting."
      },
      {
        "actionText": "I will Slack my manager letting them know the client wants more features and ask how they want me to respond to the client's email.",
        "category": "Problem Delivery",
        "feedback": "Failure. Upward delegation. You acted as a pure messenger. Managers need solutions and data, not just forwarded client demands."
      },
      {
        "actionText": "I will request a Monday morning alignment meeting with the client, my manager, and the development team before we decide on any course of action.",
        "category": "Approval Paralysis",
        "feedback": "Failure. Stalling. By delaying the decision until Monday, you guarantee the deadline is missed and force your manager into an unprepared confrontation."
      },
      {
        "actionText": "I will tell my manager that the Sales team oversold the account again, and it is unfair that we are being forced to deal with this.",
        "category": "Accountability Void",
        "feedback": "Failure. Blame shifting. Complaining about Sales does not solve the Monday morning deadline or manage the client's immediate expectations."
      }
    ]
  },
  {
    "id": "lead_04",
    "scenarioContext": "Sales and Product are deadlocked. Sales refuses to sign off on a release because it lacks a revenue feature; Product refuses to build it due to tech debt.",
    "options": [
      {
        "actionText": "I will map out Option A (Product's timeline) and Option B (Sales' revenue goal), recommend a phased rollout that bridges both, and present the compromise to my manager.",
        "category": "Optimal",
        "feedback": "Correct. Extreme ownership. You de-escalated a political turf war by translating emotional arguments into a structured, phased operational framework."
      },
      {
        "actionText": "I will tell my manager that Sales and Product are fighting and won't sign off, and ask if they can step in and force a decision.",
        "category": "Problem Delivery",
        "feedback": "Failure. Upward delegation. You abandoned your role as a mediator and simply threw the interpersonal conflict onto your manager's lap."
      },
      {
        "actionText": "I will set up a daily 45-minute sync with both VPs over the next week to slowly build consensus and ensure everyone feels heard.",
        "category": "Approval Paralysis",
        "feedback": "Failure. Meeting bloat. Attempting to solve a hard structural deadlock with endless daily meetings drains executive bandwidth and stalls execution."
      },
      {
        "actionText": "I will side with Product and tell my manager that Sales is being entirely unreasonable and ignoring our engineering constraints.",
        "category": "Accountability Void",
        "feedback": "Failure. Tribalism. Taking sides and blaming the other department destroys cross-functional alignment and exacerbates the deadlock."
      }
    ]
  },
  {
    "id": "lead_05",
    "scenarioContext": "A massive PR leak occurs regarding an upcoming, highly confidential product that is only 50% finished.",
    "options": [
      {
        "actionText": "I will analyze the leaked data, draft Option A (issue a formal denial) and Option B (accelerate a teaser announcement), recommend Option B, and provide a drafted press release to my manager.",
        "category": "Optimal",
        "feedback": "Correct. Extreme ownership. You brought order to a chaotic crisis by providing two distinct strategic pathways and the collateral needed to execute the best one immediately."
      },
      {
        "actionText": "I will forward the leaked article to my manager with the subject line 'URGENT: We have a leak, what is our PR strategy?'",
        "category": "Problem Delivery",
        "feedback": "Failure. Upward delegation. During a crisis, asking an open-ended strategic question forces your manager to start from scratch."
      },
      {
        "actionText": "I will schedule an immediate 2-hour sync with Legal, PR, and Product to debate the optics of our response before doing anything.",
        "category": "Approval Paralysis",
        "feedback": "Failure. Consensus dependency. A 2-hour debate during a fast-moving PR crisis ensures you lose control of the narrative while waiting for perfect alignment."
      },
      {
        "actionText": "I will inform leadership that the external marketing agency clearly leaked it and demand that we fire them immediately.",
        "category": "Accountability Void",
        "feedback": "Failure. Witch-hunting. Launching an unproven investigation into the source of the leak ignores the immediate operational need to manage the public fallout."
      }
    ]
  },
  {
    "id": "lead_06",
    "scenarioContext": "A critical shipment of physical marketing assets (booth, banners) is lost by the courier the day before a massive trade show.",
    "options": [
      {
        "actionText": "I will contact local printers, secure quotes for Option A (rush reprint basic assets) and Option B (rent digital displays), recommend Option A, and ask my manager for emergency budget approval.",
        "category": "Optimal",
        "feedback": "Correct. Extreme ownership. You handled the logistical panic autonomously and only escalated to your manager for the specific financial authority required to execute."
      },
      {
        "actionText": "I will text my manager: 'FedEx lost the booth materials for tomorrow. What do you want me to do?'",
        "category": "Problem Delivery",
        "feedback": "Failure. Upward delegation. You presented a dead-end problem. Your manager is now forced to act as your logistics coordinator."
      },
      {
        "actionText": "I will call an emergency team meeting to ask everyone for ideas on how to run a booth without any branded assets.",
        "category": "Approval Paralysis",
        "feedback": "Failure. Abdication of leadership. Brainstorming how to fail gracefully wastes time when you should be actively sourcing replacements."
      },
      {
        "actionText": "I will email the logistics team and blame them for not using a premium courier service for such an important shipment.",
        "category": "Accountability Void",
        "feedback": "Failure. Blame shifting. Yelling at the logistics team does not materialize a booth for the trade show happening tomorrow."
      }
    ]
  },
  {
    "id": "lead_07",
    "scenarioContext": "A new compliance law passes unexpectedly, immediately rendering your primary software UI illegal in the European Union.",
    "options": [
      {
        "actionText": "I will consult Engineering to map Option A (geoblock the EU temporarily) and Option B (deploy a bare-bones compliant UI patch). I will recommend Option A to mitigate immediate legal risk and send to my manager.",
        "category": "Optimal",
        "feedback": "Correct. Extreme ownership. You triaged a severe legal crisis by offering a fast, technical stopgap and a long-term fix, prioritizing immediate risk mitigation."
      },
      {
        "actionText": "I will email my manager saying, 'The new EU law makes our UI illegal. What is our next step for compliance?'",
        "category": "Problem Delivery",
        "feedback": "Failure. Upward delegation. You recognized the legal threat but offered zero technical or strategic pathways to solve it."
      },
      {
        "actionText": "I will try to schedule a sync with the EU compliance board, internal legal, and the design team to reach a unanimous decision on a new UI.",
        "category": "Approval Paralysis",
        "feedback": "Failure. Stalling. Seeking cross-departmental unanimous consent while the company is actively breaking international law is dangerously slow."
      },
      {
        "actionText": "I will blame the Legal department for not catching the regulatory change sooner and letting us build a non-compliant product.",
        "category": "Accountability Void",
        "feedback": "Failure. Scapegoating. Pointing fingers at Legal does not change the fact that your product is currently violating international regulations."
      }
    ]
  },
  {
    "id": "lead_08",
    "scenarioContext": "The lead developer quits abruptly, taking all undocumented knowledge of a legacy system that has just crashed.",
    "options": [
      {
        "actionText": "I will assess the outage and present Option A (rollback to last week's stable build) and Option B (hire an emergency contractor). I will recommend the rollback to restore service instantly and await approval.",
        "category": "Optimal",
        "feedback": "Correct. Extreme ownership. You circumvented the knowledge gap by proposing pragmatic operational workarounds to restore immediate functionality."
      },
      {
        "actionText": "I will message my manager: 'The lead dev left and the legacy system is down. Who should fix it?'",
        "category": "Problem Delivery",
        "feedback": "Failure. Upward delegation. You highlighted a massive single point of failure and then expected your manager to magically produce a technical solution."
      },
      {
        "actionText": "I will set up an emergency triage committee to debate system architecture and documentation practices before touching anything.",
        "category": "Approval Paralysis",
        "feedback": "Failure. Process over outcome. Debating documentation theory while a production system is down is a catastrophic misallocation of time."
      },
      {
        "actionText": "I will blame HR for their poor retention strategies, stating that this crash is their fault for letting the developer leave.",
        "category": "Accountability Void",
        "feedback": "Failure. Deflection. Complaining about HR's retention policies offers zero technical value during a live system outage."
      }
    ]
  },
  {
    "id": "lead_09",
    "scenarioContext": "An enterprise client threatens to churn because their dedicated account manager had to go on sudden emergency medical leave.",
    "options": [
      {
        "actionText": "I will review their account health, present Option A (assign our senior AM temporarily) and Option B (offer a 10% discount to buy patience), recommend Option A, and draft the client intro email.",
        "category": "Optimal",
        "feedback": "Correct. Extreme ownership. You identified the revenue risk, crafted a coverage plan, and prepared the exact communication collateral your manager needs."
      },
      {
        "actionText": "I will email my manager: 'The client is threatening to leave because John is out on medical leave. Please advise.'",
        "category": "Problem Delivery",
        "feedback": "Failure. Upward delegation. You acted as an alarm bell without functioning as a strategist, pushing all client retention work upward."
      },
      {
        "actionText": "I will schedule multiple internal meetings with the CS team to figure out who has the capacity and willingness to take on the account.",
        "category": "Approval Paralysis",
        "feedback": "Failure. Polling for volunteers during a churn crisis takes too long. Leaders must assign resources decisively, not wait for consensus."
      },
      {
        "actionText": "I will complain that the client is being completely unreasonable and lacks empathy for a medical emergency.",
        "category": "Accountability Void",
        "feedback": "Failure. Moralizing. Evaluating the client's morality does not protect the company's revenue. You abandoned your operational duty."
      }
    ]
  },
  {
    "id": "lead_10",
    "scenarioContext": "The Legal department flags a massive liability clause in a contract that was just signed by a major client. It requires immediate renegotiation.",
    "options": [
      {
        "actionText": "I will map the liability, present Option A (draft an addendum) and Option B (void and reissue), recommend the addendum, draft it with Legal, and send to my manager for review.",
        "category": "Optimal",
        "feedback": "Correct. Extreme ownership. You contained a legal disaster by partnering with the SME (Legal) and presenting a finalized mitigation strategy to your manager."
      },
      {
        "actionText": "I will forward Legal's email to my manager with the note: 'Legal says this contract is bad. What should we do?'",
        "category": "Problem Delivery",
        "feedback": "Failure. Upward delegation. You acted as an email router rather than a manager, transferring a complex legal risk entirely onto your boss."
      },
      {
        "actionText": "I will schedule a full review board with Legal, Sales, and the client to openly discuss how the clause slipped through.",
        "category": "Approval Paralysis",
        "feedback": "Failure. Witch-hunting disguised as alignment. Holding a massive committee meeting exposes internal incompetence to the client and delays the fix."
      },
      {
        "actionText": "I will blame the junior sales representative who originally drafted the agreement for missing the clause.",
        "category": "Accountability Void",
        "feedback": "Failure. Blame shifting. Throwing a junior employee under the bus does not alter the fact that the company is currently exposed to massive legal liability."
      }
    ]
  },
  {
    "id": "lead_11",
    "scenarioContext": "A sudden corporate budget freeze halts the hiring process for a critical open role required for your Q4 deliverables.",
    "options": [
      {
        "actionText": "I will analyze the workload and present Option A (reallocate internal resources by pausing Project X) or Option B (hire an offshore contractor from a different budget). I will recommend Option A.",
        "category": "Optimal",
        "feedback": "Correct. Extreme ownership. You respected the financial boundary while finding creative operational trade-offs to ensure Q4 goals are still addressed."
      },
      {
        "actionText": "I will ask my manager: 'HR froze our open requisition. How do you expect us to hit our Q4 goals now?'",
        "category": "Problem Delivery",
        "feedback": "Failure. Upward delegation. You responded to a constraint with a complaint, forcing your manager to solve your resource planning problem."
      },
      {
        "actionText": "I will request a weekly alignment sync with Finance to constantly monitor the budget and see if it thaws before Q4.",
        "category": "Approval Paralysis",
        "feedback": "Failure. Passive waiting. Relying on endless check-ins with Finance means you are actively choosing not to execute your Q4 deliverables in the present."
      },
      {
        "actionText": "I will document that Finance ruined the department's Q4 delivery capability and preemptively state that we will miss our targets.",
        "category": "Accountability Void",
        "feedback": "Failure. Defeatism. Using a budget freeze as an excuse to completely abdicate responsibility for your departmental targets is a massive leadership failure."
      }
    ]
  },
  {
    "id": "lead_12",
    "scenarioContext": "The primary database is corrupted during a routine migration, and you discover the last successful backup is 12 hours old.",
    "options": [
      {
        "actionText": "I will evaluate the data loss and present Option A (restore the 12h backup and manually reconstruct via logs) or Option B (attempt a risky deep-recovery tool). I will recommend Option A to guarantee stability.",
        "category": "Optimal",
        "feedback": "Correct. Extreme ownership. You diagnosed a catastrophic technical failure and presented a clear, risk-adjusted path forward for executive sign-off."
      },
      {
        "actionText": "I will message my manager: 'The DB is corrupted and our backups are old. What is the call here?'",
        "category": "Problem Delivery",
        "feedback": "Failure. Upward delegation. You forced a non-technical executive to make a highly technical crisis decision without providing any context or recommendations."
      },
      {
        "actionText": "I will schedule a bridge call with all engineering leads to debate database recovery theory and reach a consensus on best practices.",
        "category": "Approval Paralysis",
        "feedback": "Failure. Academic stalling. Debating theory while a production database is offline destroys revenue and customer trust by the minute."
      },
      {
        "actionText": "I will blame the infrastructure team for not setting up hourly snapshots, ensuring everyone knows this wasn't my fault.",
        "category": "Accountability Void",
        "feedback": "Failure. Blame shifting. Protecting your own ego by attacking infrastructure does absolutely nothing to restore the corrupted data."
      }
    ]
  },
  {
    "id": "lead_13",
    "scenarioContext": "A senior executive unexpectedly rejects your roadmap proposal during a live presentation and demands a total rewrite by tomorrow morning.",
    "options": [
      {
        "actionText": "I will deconstruct the exec's feedback, present my manager with Option A (pivot entirely to the Exec's vision) and Option B (a hybrid approach), and recommend Option B with a draft timeline.",
        "category": "Optimal",
        "feedback": "Correct. Extreme ownership. You absorbed public criticism professionally and immediately translated it into a structured, actionable revision strategy."
      },
      {
        "actionText": "I will go to my manager and ask: 'The VP hated the roadmap. How do you want me to rewrite it for tomorrow?'",
        "category": "Problem Delivery",
        "feedback": "Failure. Upward delegation. You surrendered your autonomy entirely and asked your manager to dictate your work step-by-step."
      },
      {
        "actionText": "I will set up daily check-ins with the VP to co-create the roadmap moving forward so they don't reject it again.",
        "category": "Approval Paralysis",
        "feedback": "Failure. Fear-based stalling. Forcing a VP to co-create your work because you are afraid of rejection is a massive drain on executive bandwidth."
      },
      {
        "actionText": "I will blame my manager, pointing out that they approved the original roadmap before I presented it.",
        "category": "Accountability Void",
        "feedback": "Failure. Insubordination. Throwing your direct manager under the bus after a failed presentation guarantees you lose all political capital and trust."
      }
    ]
  },
  {
    "id": "lead_14",
    "scenarioContext": "A major competitor launches a clone of your upcoming core feature at half the price, exactly two weeks before your launch.",
    "options": [
      {
        "actionText": "I will analyze their pricing, present Option A (undercut their price) and Option B (maintain price but highlight our premium integrations), recommend Option B, and draft a new sales battlecard.",
        "category": "Optimal",
        "feedback": "Correct. Extreme ownership. You responded to a massive external threat with a calm, strategic analysis and prepared the exact enablement materials needed to compete."
      },
      {
        "actionText": "I will Slack my manager: 'Competitor X just dropped our exact feature for cheaper. What is our counter-strategy?'",
        "category": "Problem Delivery",
        "feedback": "Failure. Upward delegation. You demanded a strategic pivot from your manager without offering any data, analysis, or market research."
      },
      {
        "actionText": "I will call a 2-day offsite with marketing and product to reimagine the entire go-to-market strategy before we launch.",
        "category": "Approval Paralysis",
        "feedback": "Failure. Overreaction. Halting a launch two weeks out for a massive offsite guarantees you lose market share and creates internal chaos."
      },
      {
        "actionText": "I will blame Product for taking too long to build the feature, claiming we lost our first-mover advantage because of their slow sprints.",
        "category": "Accountability Void",
        "feedback": "Failure. Cross-departmental hostility. Blaming Product for market dynamics is toxic and provides zero strategic value to the upcoming launch."
      }
    ]
  },
  {
    "id": "lead_15",
    "scenarioContext": "You discover a major software bug that is actively charging customer credit cards twice for a single transaction.",
    "options": [
      {
        "actionText": "I will isolate the gateway issue, present Option A (freeze all payments immediately) and Option B (allow payments but script auto-refunds), and recommend Option A to stop legal liability.",
        "category": "Optimal",
        "feedback": "Correct. Extreme ownership. You identified a critical financial/legal risk and presented a decisive operational solution to stop the bleeding instantly."
      },
      {
        "actionText": "I will message my manager: 'Customers are being double-charged. Should we turn off the payment gateway?'",
        "category": "Problem Delivery",
        "feedback": "Failure. Upward delegation. By framing this as a question rather than a recommendation, you abdicated your responsibility during a high-stakes financial crisis."
      },
      {
        "actionText": "I will schedule a risk-assessment meeting with Legal, CS, and Engineering to reach a consensus on how much downtime is acceptable.",
        "category": "Approval Paralysis",
        "feedback": "Failure. Lethal stalling. While you schedule a meeting, the company is actively stealing money from customers. This requires immediate action, not consensus."
      },
      {
        "actionText": "I will blame Stripe (or the payment processor) for the API malfunction and wait for them to fix it.",
        "category": "Accountability Void",
        "feedback": "Failure. Deflection. Passing the blame to a vendor while your customers are actively being robbed destroys your brand and creates massive liability."
      }
    ]
  },
  {
    "id": "lead_16",
    "scenarioContext": "You realize the Marketing department has overspent their monthly digital ad budget by 30% due to an automated bidding error.",
    "options": [
      {
        "actionText": "I will halt the algorithm, present Option A (cut all remaining ad spend for the month) and Option B (pull funds from the Q4 event budget to maintain lead flow), and recommend Option B.",
        "category": "Optimal",
        "feedback": "Correct. Extreme ownership. You stopped the financial bleeding and proposed a realistic, cross-budget reallocation to ensure business continuity."
      },
      {
        "actionText": "I will email my manager: 'The auto-bidder went crazy and we are 30% over budget. What do we do?'",
        "category": "Problem Delivery",
        "feedback": "Failure. Upward delegation. You delivered a raw financial failure without doing the hard work of finding where to cut costs to balance the ledger."
      },
      {
        "actionText": "I will arrange a series of budget review meetings with Finance to manually approve every future ad campaign before it launches.",
        "category": "Approval Paralysis",
        "feedback": "Failure. Over-correction. Instituting massive bureaucratic red tape to solve a single algorithm error will completely throttle your marketing velocity."
      },
      {
        "actionText": "I will blame the external ad agency for setting up the automated bidding scripts incorrectly and demand they refund us.",
        "category": "Accountability Void",
        "feedback": "Failure. Abdication of oversight. As the internal owner of the budget, blaming an agency for a script you failed to monitor shows weak leadership."
      }
    ]
  },
  {
    "id": "lead_17",
    "scenarioContext": "Your engineering team is locked out of the primary AWS production environment because a core admin credential was compromised.",
    "options": [
      {
        "actionText": "I will trigger incident response, present Option A (burn all current keys and force a global reset) and Option B (isolate the compromised IAM role), and recommend Option A for zero-trust security.",
        "category": "Optimal",
        "feedback": "Correct. Extreme ownership. You initiated security protocols and gave your manager a clear, risk-weighted binary choice to contain a massive cyber threat."
      },
      {
        "actionText": "I will text my manager: 'Someone compromised our AWS admin account. What is the protocol here?'",
        "category": "Problem Delivery",
        "feedback": "Failure. Upward delegation. Asking for basic protocol during an active security breach proves you are entirely unprepared for crisis management."
      },
      {
        "actionText": "I will set up an emergency committee to review access logs before deciding whether to revoke credentials.",
        "category": "Approval Paralysis",
        "feedback": "Failure. Paralysis by analysis. Reviewing logs while an attacker has active admin access is a catastrophic failure of basic cybersecurity prioritization."
      },
      {
        "actionText": "I will blame the former DevOps engineer for not rotating the root passwords before they left the company.",
        "category": "Accountability Void",
        "feedback": "Failure. Useless deflection. Blaming an ex-employee does absolutely nothing to evict the active threat actor from your production environment."
      }
    ]
  }
];